import TransactionCheckoutHeader from "../components/TransactionCheckoutHeader";
import Warning from "../components/Warning";
import Alert from "../components/Alert";
import RefundConversionNotice from "../components/RefundConversionNotice";
import { useState, useEffect, useRef } from "react";
import PrimaryButton from "../components/PrimaryButton";
import {
  ResponseNotOkError,
  Session,
  makeRequest,
} from "@paywithglide/glide-js";
import {
  Copy,
  CheckCheck,
  CircleCheckBig,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useGlideDepositConfig } from "../lib/useDepositConfig";
import { logger } from "../lib/logger";
import { refundChainOf } from "../lib/refundChain";

type Props = {
  session: Session;
  refundToken?: string;
};

export default function InsufficientPaymentAmount({
  session,
  refundToken,
}: Props) {
  const { glideConfig, ...depositConfig } = useGlideDepositConfig();
  const isRailsApp = depositConfig.appName === "Rails";
  const isCustomPendingRefundCopy =
    isRailsApp && session.paymentStatus === "pending_refund";
  const [refundAddress, setRefundAddress] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [didCopyPaymentAddress, setDidCopyPaymentAddress] = useState(false);
  const [refundSubmitted, setRefundSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);
  const refundInputRef = useRef<HTMLInputElement>(null);
  const refundChain = refundChainOf(session);

  const onClick = async () => {
    if (refundAddress.trim()) {
      setIsLoading(true);
      setErrorMessage(null);
      try {
        await makeRequest(glideConfig, {
          url: `/sessions/${session.sessionId}/refund`,
          method: "POST",
          body: {
            refundAddress: refundAddress.trim(),
            refundToken,
          },
        });
        setRefundSubmitted(true);
      } catch (error) {
        logger.error(
          `[InsufficientPaymentAmount] Failed to submit refund request: ${error}`,
        );
        if (error instanceof ResponseNotOkError) {
          const rawBody = error.response;
          try {
            const errorData = JSON.parse(rawBody);
            if (errorData.error) {
              setErrorMessage(errorData.error);
            } else {
              setErrorMessage(
                "Failed to submit refund request. Please try again.",
              );
            }
          } catch {
            setErrorMessage(
              "Failed to submit refund request. Please try again.",
            );
          }
        } else {
          setErrorMessage("Failed to submit refund request. Please try again.");
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const onCopyPaymentAddress = () => {
    if (session.depositAddress) {
      navigator.clipboard.writeText(session.depositAddress);
      setDidCopyPaymentAddress(true);
      setTimeout(() => setDidCopyPaymentAddress(false), 3000);
    }
  };

  useEffect(() => {
    // Focus the refund input when component mounts and refund hasn't been submitted
    if (!refundSubmitted && refundInputRef.current) {
      refundInputRef.current.focus();
    }
  }, [refundSubmitted]);

  return (
    <>
      {depositConfig.embed ? null : (
        <div className="mt-2 mb-4 flex flex-col items-center justify-center">
          <img
            src={depositConfig.appLogoUrl}
            className="h-[--app-logo-height] w-auto"
            alt={`${depositConfig.appName} Logo`}
          />
        </div>
      )}

      <TransactionCheckoutHeader
        title={
          isCustomPendingRefundCopy ? "Refund Eligible" : "Transaction Failed"
        }
      />

      <Warning className="my-4">
        {isCustomPendingRefundCopy ? (
          <>
            <div className="font-medium mb-1">Action Required</div>
            <div>
              We are issuing the full 100% refund of your evaluation purchase to
              your provided wallet address.
            </div>
            <div className="mt-3">
              We appreciate your participation in Play. If you have questions,
              our support team is available to assist. Should you have any
              issues, please contact us directly:{" "}
              <a
                href="https://help.rails.xyz/contactus"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2"
              >
                https://help.rails.xyz/contactus
              </a>
            </div>
          </>
        ) : (
          <>
            <div className="font-medium mb-1">Incorrect Payment Amount</div>
            <div>
              Your payment amount was incorrect. Enter your refund address below
              to get your payment back.
            </div>
          </>
        )}
      </Warning>

      {refundSubmitted ? (
        <Alert
          icon={<CircleCheckBig strokeWidth={2.25} className="w-4 h-4" />}
          title="Refund Request Submitted"
          subtitle={`Your refund request has been submitted successfully. You should receive your refund within the next ${isRailsApp ? "30 days" : "few hours"}.`}
          className="mb-6"
        />
      ) : (
        <>
          <RefundConversionNotice session={session} className="mb-4" />

          <div>
            <div className="mb-1 font-medium text-sm text-[--color-text-primary]">
              Refund Address
            </div>
            <div className="text-xs text-[--color-text-secondary] mb-3">
              Enter your {refundChain.name} refund address. Your refund will be
              sent in the next {isRailsApp ? "30 days" : "few hours"}.
            </div>

            <div className="flex items-center gap-1 bg-[--color-background-secondary] p-2 rounded-md mb-4">
              {refundChain.logoUrl && (
                <img
                  src={refundChain.logoUrl}
                  alt={refundChain.name}
                  className="h-4 w-4 rounded-full flex-shrink-0"
                />
              )}
              <input
                ref={refundInputRef}
                className="flex-1 bg-transparent text-[--color-text-primary] text-sm outline-none placeholder:text-[--color-text-secondary]"
                value={refundAddress}
                onChange={(e) => setRefundAddress(e.target.value)}
                placeholder={refundChain.placeholder}
              />
            </div>
          </div>

          <PrimaryButton
            onClick={() => onClick()}
            loading={isLoading}
            disabled={!refundAddress.trim()}
            className="mt-4"
          >
            {refundAddress.trim()
              ? "Refund Transaction"
              : "Enter refund address"}
          </PrimaryButton>

          {errorMessage && (
            <Warning className="mt-4">
              <div className="font-medium mb-1">Error</div>
              <div>{errorMessage}</div>
            </Warning>
          )}
        </>
      )}

      <button
        onClick={() => setShowTransactionDetails(!showTransactionDetails)}
        className="flex items-center gap-0.5 text-[--color-text-secondary] hover:text-[--color-text-primary] transition-colors text-xs h-7 mt-4"
      >
        {showTransactionDetails ? (
          <>
            <ChevronUp className="h-4 w-4" /> Hide transaction details
          </>
        ) : (
          <>
            <ChevronDown className="h-4 w-4" /> Show transaction details
          </>
        )}
      </button>

      {showTransactionDetails ? (
        <>
          <div className="mt-1">
            <div className="flex justify-between items-center h-7">
              <div className="text-xs text-[--color-text-secondary]">
                Payment Chain
              </div>
              <div className="flex items-center gap-1">
                {session.actualPaymentChainLogoUrl && (
                  <img
                    src={session.actualPaymentChainLogoUrl}
                    alt={session.actualPaymentChainName}
                    className="h-4 w-4 rounded-full flex-shrink-0"
                  />
                )}
                <span className="text-xs text-[--color-text-primary]">
                  {session.actualPaymentChainName}
                </span>
              </div>
            </div>

            <div className="flex justify-between items-center h-7">
              <div className="text-xs text-[--color-text-secondary]">
                Payment Address
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="p-1 hover:bg-[--color-background-secondary] rounded transition-colors"
                  onClick={onCopyPaymentAddress}
                  aria-label="Copy payment address"
                >
                  {didCopyPaymentAddress ? (
                    <CheckCheck
                      className="w-4 h-4 text-green-500"
                      strokeWidth={2}
                    />
                  ) : (
                    <Copy
                      className="w-4 h-4 text-[--color-text-secondary]"
                      strokeWidth={2}
                    />
                  )}
                </button>
                <div className="text-xs text-[--color-text-primary]">
                  {session.depositAddress && session.depositAddress.length > 20
                    ? `${session.depositAddress.slice(0, 8)}...${session.depositAddress.slice(-8)}`
                    : session.depositAddress}
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center h-7">
              <div className="text-xs text-[--color-text-secondary]">
                Expected Amount
              </div>
              <div className="text-xs text-[--color-text-primary]">
                {session.paymentAmount} {session.paymentCurrencySymbol} ($
                {Number(session.paymentAmountUSD).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
                )
              </div>
            </div>

            <div className="flex justify-between items-center h-7">
              <div className="text-xs text-[--color-text-secondary]">
                Your Payment
              </div>
              <div className="text-xs text-[--color-text-primary]">
                {session.actualPaymentAmount}{" "}
                {session.actualPaymentCurrencySymbol} ($
                {Number(session.actualPaymentAmountUSD).toLocaleString(
                  undefined,
                  {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  },
                )}
                )
              </div>
            </div>
          </div>
        </>
      ) : null}
    </>
  );
}
